import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Loader2, AlertTriangle, CheckCircle2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

const RISK_COLORS = { critical: "#DC2626", high: "#D97706", medium: "#F59E0B", low: "#10B981" };

export default function PredictivePage({ onLogout, userRole, isFeatureEnabled }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try { const r = await axios.get(`${API}/predictive-maintenance`, { headers: getAuthHeaders() }); setData(r.data); }
    catch { toast.error("Failed to load predictions"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#2563EB]" size={28} /></div></Layout>;

  const summary = data?.summary || {};
  const predictions = data?.predictions || [];

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="predictive-page">
        <PageHeader title="Predictive Maintenance" subtitle="Machine risk analysis and failure prediction" testId="predictive-title" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="predictive-stats">
          {[["Total Machines", summary.total_machines, "#0F172A"], ["High Risk", summary.high_risk, "#DC2626"], ["Medium Risk", summary.medium_risk, "#F59E0B"], ["Avg Risk Score", summary.avg_risk, "#2563EB"]].map(([l, v, c]) => (
            <div key={l} className="bg-white border border-[#E2E8F0] px-4 py-3"><p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{l}</p><p className="text-2xl font-heading font-black mt-0.5" style={{ color: c }}>{v || 0}</p></div>
          ))}
        </div>
        {predictions.length === 0 ? <div className="bg-white border border-[#E2E8F0] p-12 text-center text-sm text-[#94A3B8]"><TrendingUp size={36} className="mx-auto mb-2 text-[#CBD5E1]" />No machine data available for analysis.</div> : (
          <div className="space-y-3">
            {predictions.map(p => (
              <div key={p.machine_id} className="bg-white border border-[#E2E8F0] p-4" data-testid={`prediction-${p.machine_number}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-bold text-[#0F172A]">{p.machine_number}</span>
                      {p.machine_name && <span className="text-xs text-[#64748B]">{p.machine_name}</span>}
                      <span className="text-[10px] px-2 py-0.5 font-bold uppercase" style={{ color: RISK_COLORS[p.risk_level], backgroundColor: `${RISK_COLORS[p.risk_level]}15`, border: `1px solid ${RISK_COLORS[p.risk_level]}40` }}>{p.risk_level}</span>
                    </div>
                    <div className="flex gap-4 mt-1.5 text-[11px] text-[#64748B]">
                      <span>WOs: {p.stats.total_wos} (open: {p.stats.open_wos})</span>
                      <span>Maintenance: {p.stats.total_maintenance} (overdue: {p.stats.overdue_maintenance})</span>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 border-4 flex items-center justify-center" style={{ borderColor: RISK_COLORS[p.risk_level] }}>
                      <span className="text-xl font-black" style={{ color: RISK_COLORS[p.risk_level] }}>{p.risk_score}</span>
                    </div>
                    <span className="text-[8px] text-[#94A3B8] uppercase">risk</span>
                  </div>
                </div>
                {p.factors.length > 0 && (
                  <div className="mt-2 flex gap-1.5 flex-wrap">{p.factors.map((f, i) => <span key={i} className="text-[10px] px-2 py-0.5 bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]"><AlertTriangle size={9} className="inline mr-0.5" />{f}</span>)}</div>
                )}
                {p.suggestions.length > 0 && (
                  <div className="mt-2 space-y-0.5">{p.suggestions.map((s, i) => <p key={i} className="text-[10px] text-[#10B981] flex items-center gap-1"><CheckCircle2 size={10} />{s}</p>)}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
